// src/components/CommunityVoice.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Flag, Gavel } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import './CommunityVoice.css';

/* ── Helpers ── */

// 'YYYY-MM-DD' (date-only) -> "June 24, 2025" with no timezone drift.
const formatDateOnly = (iso) => {
  if (!iso) return '';
  const parts = String(iso).slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !n)) return String(iso);
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
};

const stanceClass = (stance) => {
  const s = String(stance || '').toLowerCase();
  if (s === 'support') return 'cv-stance-support';
  if (s === 'oppose') return 'cv-stance-oppose';
  if (s === 'mixed') return 'cv-stance-mixed';
  return 'cv-stance-neutral';
};

const sentimentClass = (sentiment) => {
  const s = String(sentiment || '').toLowerCase();
  if (s === 'positive') return 'cv-sent-positive';
  if (s === 'negative') return 'cv-sent-negative';
  if (s === 'mixed') return 'cv-sent-mixed';
  return 'cv-sent-neutral';
};

const STANCE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'support', label: 'Support' },
  { id: 'oppose', label: 'Oppose' },
];

/* ── Theme ↔ follow-up matching (Topics / Votings) ──
   Themes are short free-text tags ("affordable housing funding"); Topics titles
   and Votings issues are longer prose. We match on significant-word overlap:
   a multi-word theme needs ≥2 of its words in the target, a one-word theme
   needs its word. Words compare after lowercasing, dropping stopwords, and
   trimming a plural "s"; longer words also match as prefixes ("regulation" ~
   "regulations"). Deliberately simple — it runs client-side per render. */
const STOPWORDS = new Set([
  'and', 'or', 'of', 'the', 'a', 'an', 'in', 'on', 'for', 'to', 'at',
  'with', 'from', 'amp', 'per', 'item', 'items',
]);

const significantTokens = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w))
    .map((w) => w.replace(/s$/, ''));

const wordsMatch = (a, b) =>
  a === b || (a.length >= 5 && b.length >= 5 && (a.startsWith(b) || b.startsWith(a)));

const themeMatchesText = (themeTokens, targetTokens) => {
  if (!themeTokens.length || !targetTokens.length) return false;
  let hits = 0;
  for (const t of themeTokens) {
    if (targetTokens.some((w) => wordsMatch(t, w))) hits += 1;
  }
  return hits >= Math.min(2, themeTokens.length);
};

/* ── Subject qualifier: the proper-noun phrase the theme's comments are
   actually about ("community centers" → "Ella Hill Hutch"). We scan the
   theme's comment summaries + agenda items for capitalized multi-word
   phrases, drop generic place names and phrases that just restate the theme,
   and keep the one mentioned in the most comments (min 2). ── */
const SUMMARY_VERBS = new Set([
  'supported', 'opposed', 'urged', 'said', 'described', 'asked', 'requested',
  'argued', 'criticized', 'thanked', 'called', 'emphasized', 'raised',
  'expressed', 'noted', 'stated', 'shared', 'invited', 'alleged', 'framed',
  'warned', 'demanded', 'encouraged', 'highlighted', 'celebrated', 'discussed',
]);
const GENERIC_PHRASES = new Set([
  'san francisco', 'board of supervisors', 'city hall', 'bay area',
  'united states', 'city and county',
]);

function themeQualifier(theme, items) {
  const themeTokens = significantTokens(theme);
  const counts = new Map(); // lowercased phrase -> { display, rows }
  items.forEach((it) => {
    const seenThisRow = new Set();
    const consider = (phrase) => {
      let p = phrase.trim();
      // Strip "Item 24 (" / "File 251003:" wrappers and stray brackets/punct.
      p = p.replace(/^(?:Item|File)\s+\d+\S*\s*[:(–—-]?\s*/i, '');
      p = p.replace(/^[([]\s*/, '').replace(/[)\]]\s*$/, '').replace(/[.,;:]$/, '');
      // Drop a leading summary verb ("Supported Booker T." → "Booker T.")
      const words = p.split(/\s+/);
      if (words.length > 1 && SUMMARY_VERBS.has(words[0].toLowerCase())) {
        words.shift();
        p = words.join(' ');
      }
      if (words.length < 2 || p.length < 6 || p.length > 50) return;
      const key = p.toLowerCase();
      if (GENERIC_PHRASES.has(key) || seenThisRow.has(key)) return;
      // Skip phrases that only restate the theme ("Affordable Housing")
      const pTokens = significantTokens(p);
      if (pTokens.length && pTokens.every((t) => themeTokens.some((w) => wordsMatch(t, w)))) return;
      seenThisRow.add(key);
      const e = counts.get(key) || { display: p, rows: 0 };
      e.rows += 1;
      counts.set(key, e);
    };
    if (it.agenda_item) consider(it.agenda_item);
    const text = `${it.agenda_item || ''}. ${it.summary || ''}`;
    // Capitalized runs, allowing lowercase connectors mid-phrase
    // ("American Muslim Appreciation and Awareness Month").
    const matches = text.match(
      /[A-Z][\w'’.-]*(?:\s+(?:and|of|the|for|&)\s+[A-Z][\w'’.-]*|\s+[A-Z][\w'’.-]*)+/g
    ) || [];
    matches.forEach(consider);
  });
  let best = null;
  for (const e of counts.values()) {
    if (e.rows < 2) continue;
    if (!best || e.rows > best.rows ||
        (e.rows === best.rows && e.display.length > best.display.length)) {
      best = e;
    }
  }
  return best ? best.display : null;
}

function CommunityVoice() {
  const { abbreviation } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  // Follow-up sources: current Topics and later Votings for this district.
  // Best-effort — a failure here only hides the flags, never the comments.
  const [topics, setTopics] = useState([]);
  const [votings, setVotings] = useState([]);

  const [themeFilter, setThemeFilter] = useState(null);
  const [complaintsOnly, setComplaintsOnly] = useState(false);
  const [stanceFilter, setStanceFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      const { data: district, error: distError } = await supabase
        .from('Districts')
        .select('id')
        .eq('abbreviation', abbreviation)
        .single();

      if (cancelled) return;
      if (distError || !district) {
        setError(distError || { message: 'District not found' });
        setLoading(false);
        return;
      }

      const [commentsRes, topicsRes, votingsRes] = await Promise.all([
        supabase
          .from('public_comments')
          .select('*')
          .eq('districtId', district.id)
          .order('meeting_date', { ascending: false })
          .order('id', { ascending: true })
          .limit(500),
        supabase
          .from('Topics')
          .select('id, title')
          .eq('districtId', district.id)
          .eq('current', true)
          .limit(100),
        supabase
          .from('Votings')
          .select('id, voteDate, category, issue')
          .eq('districtId', district.id)
          .order('voteDate', { ascending: false })
          .limit(800),
      ]);

      if (cancelled) return;
      if (commentsRes.error) setError(commentsRes.error);
      else setRows(commentsRes.data || []);
      // Flags are best-effort: swallow topic/voting errors silently.
      setTopics(topicsRes.error ? [] : topicsRes.data || []);
      setVotings(votingsRes.error ? [] : votingsRes.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [abbreviation]);

  /* ── Aggregates (from all rows, unfiltered) ── */
  const stats = useMemo(() => {
    const meetingKeys = new Set();
    let complaints = 0;
    const themeCounts = new Map();
    for (const r of rows) {
      meetingKeys.add(r.meeting_id != null ? `m:${r.meeting_id}` : `d:${r.meeting_date}`);
      if (r.is_complaint) complaints += 1;
      if (Array.isArray(r.themes)) {
        for (const t of r.themes) {
          if (!t) continue;
          themeCounts.set(t, (themeCounts.get(t) || 0) + 1);
        }
      }
    }
    const sortedThemes = Array.from(themeCounts.entries()).sort((a, b) => b[1] - a[1]);
    return {
      total: rows.length,
      meetings: meetingKeys.size,
      complaintShare: rows.length ? Math.round((complaints / rows.length) * 100) : 0,
      sortedThemes,
      topTheme: sortedThemes.length ? sortedThemes[0][0] : null,
    };
  }, [rows]);

  /* ── Themes-over-time: top 5 themes, last 6 months ── */
  const trend = useMemo(() => {
    const topThemes = stats.sortedThemes.slice(0, 5).map(([t]) => t);
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      });
    }
    // counts[monthKey][theme] = n
    const counts = {};
    for (const m of months) counts[m.key] = {};
    for (const r of rows) {
      const monthKey = String(r.meeting_date || '').slice(0, 7);
      if (!counts[monthKey]) continue;
      if (!Array.isArray(r.themes)) continue;
      for (const t of r.themes) {
        if (!topThemes.includes(t)) continue;
        counts[monthKey][t] = (counts[monthKey][t] || 0) + 1;
      }
    }
    let maxMonthTotal = 0;
    const monthRows = months.map((m) => {
      const perTheme = topThemes.map((t) => counts[m.key][t] || 0);
      const total = perTheme.reduce((a, b) => a + b, 0);
      if (total > maxMonthTotal) maxMonthTotal = total;
      return { ...m, perTheme, total };
    });
    return { topThemes, monthRows, maxMonthTotal };
  }, [rows, stats.sortedThemes]);

  /* ── Support/oppose counts per theme (top subjects by stance volume) ── */
  const stanceBySubject = useMemo(() => {
    const perTheme = new Map(); // theme -> { support, oppose, firstDate, items }
    for (const r of rows) {
      if (!Array.isArray(r.themes)) continue;
      const stance = String(r.stance || '').toLowerCase();
      for (const t of r.themes) {
        if (!t) continue;
        let e = perTheme.get(t);
        if (!e) {
          e = { theme: t, support: 0, oppose: 0, firstDate: r.meeting_date || null, items: [] };
          perTheme.set(t, e);
        }
        if (stance === 'support') e.support += 1;
        else if (stance === 'oppose') e.oppose += 1;
        if (stance === 'support' || stance === 'oppose') {
          e.items.push({ agenda_item: r.agenda_item, summary: r.summary });
        }
        if (r.meeting_date && (!e.firstDate || r.meeting_date < e.firstDate)) {
          e.firstDate = r.meeting_date;
        }
      }
    }
    const list = Array.from(perTheme.values())
      .filter((e) => e.support + e.oppose > 0)
      .sort((a, b) => (b.support + b.oppose) - (a.support + a.oppose))
      .slice(0, 8)
      .map((e) => ({ ...e, qualifier: themeQualifier(e.theme, e.items) }));
    const maxArm = list.reduce((m, e) => Math.max(m, e.support, e.oppose), 0);
    return { list, maxArm };
  }, [rows]);

  /* ── Follow-up status per displayed theme: later discussed (Topics) and/or
        later voted (Votings dated on/after the theme's first comment) ── */
  const followUp = useMemo(() => {
    const topicTokens = topics.map((t) => ({ t, tokens: significantTokens(t.title) }));
    const votingTokens = votings.map((v) => ({
      v,
      tokens: significantTokens(`${v.category || ''} ${v.issue || ''}`),
    }));
    const map = new Map();
    for (const e of stanceBySubject.list) {
      const themeTok = significantTokens(e.theme);
      const matchedTopics = topicTokens
        .filter((x) => themeMatchesText(themeTok, x.tokens))
        .map((x) => x.t);
      const matchedVotes = votingTokens
        .filter(
          (x) =>
            themeMatchesText(themeTok, x.tokens) &&
            (!e.firstDate || (x.v.voteDate && x.v.voteDate >= e.firstDate))
        )
        .map((x) => x.v);
      map.set(e.theme, { topics: matchedTopics, votes: matchedVotes });
    }
    return map;
  }, [stanceBySubject.list, topics, votings]);

  /* ── Filtered comment list, grouped by meeting date ── */
  const groups = useMemo(() => {
    const filtered = rows.filter((r) => {
      if (complaintsOnly && !r.is_complaint) return false;
      if (stanceFilter !== 'all' && String(r.stance || '').toLowerCase() !== stanceFilter) return false;
      if (themeFilter && !(Array.isArray(r.themes) && r.themes.includes(themeFilter))) return false;
      return true;
    });
    const byDate = new Map();
    const out = [];
    for (const r of filtered) {
      const key = r.meeting_date || 'unknown';
      if (!byDate.has(key)) {
        const g = { date: key, items: [] };
        byDate.set(key, g);
        out.push(g);
      }
      byDate.get(key).items.push(r);
    }
    return out;
  }, [rows, themeFilter, complaintsOnly, stanceFilter]);

  if (loading) {
    return (
      <div className="cv-tab">
        <div className="cv-card cv-status">Loading community voice…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="cv-tab">
        <div className="cv-card cv-status cv-status-error">Error: {error.message}</div>
      </div>
    );
  }

  const hasFiltersActive = Boolean(themeFilter) || complaintsOnly || stanceFilter !== 'all';

  return (
    <div className="cv-tab">
      <div className="cv-card cv-header-card">
        <h2 className="cv-title">Community Voice</h2>
        <p className="cv-subtitle">What speakers said during public comment</p>
        <p className="cv-honesty-note">
          Summaries are from meeting recordings and may contain errors. Speakers
          are described by role only — never by name.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="cv-card cv-empty">
          No public-comment data yet for this district. It appears after meetings are processed.
        </div>
      ) : (
        <>
          {/* Stat pills */}
          <div className="cv-stats">
            <div className="cv-stat">
              <span className="cv-stat-value">{stats.total}</span>
              <span className="cv-stat-label">Comments</span>
            </div>
            <div className="cv-stat">
              <span className="cv-stat-value">{stats.meetings}</span>
              <span className="cv-stat-label">Meetings covered</span>
            </div>
            <div className="cv-stat">
              <span className="cv-stat-value">{stats.complaintShare}%</span>
              <span className="cv-stat-label">Complaints</span>
            </div>
            <div className="cv-stat">
              <span className="cv-stat-value cv-stat-value-text" title={stats.topTheme || undefined}>
                {stats.topTheme || '—'}
              </span>
              <span className="cv-stat-label">Top theme</span>
            </div>
          </div>

          {/* Theme chips + filters */}
          <div className="cv-card cv-filters-card">
            {stats.sortedThemes.length > 0 && (
              <div className="cv-theme-strip">
                {stats.sortedThemes.slice(0, 15).map(([t, c]) => (
                  <button
                    key={t}
                    type="button"
                    className={`cv-chip ${themeFilter === t ? 'active' : ''}`}
                    onClick={() => setThemeFilter(themeFilter === t ? null : t)}
                  >
                    {t} <span className="cv-chip-count">{c}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="cv-filter-row">
              <button
                type="button"
                className={`cv-toggle ${complaintsOnly ? 'active' : ''}`}
                onClick={() => setComplaintsOnly((v) => !v)}
              >
                Complaints only
              </button>
              <div className="cv-stance-filter">
                {STANCE_FILTERS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`cv-stance-btn ${stanceFilter === s.id ? 'active' : ''}`}
                    onClick={() => setStanceFilter(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Support vs. opposition by subject (diverging bars + follow-up flags) */}
          {stanceBySubject.list.length > 0 && (
            <div className="cv-card cv-stance-chart">
              <h3 className="cv-section-title">Support vs. opposition by subject</h3>
              <div className="cv-sbs-legend">
                <span className="cv-legend-item">
                  <span className="cv-swatch cv-sbs-oppose-swatch" /> Oppose
                </span>
                <span className="cv-legend-item">
                  <span className="cv-swatch cv-sbs-support-swatch" /> Support
                </span>
                <span className="cv-legend-item cv-legend-flag">
                  <Flag size={13} aria-hidden="true" /> discussed at a later meeting
                </span>
                <span className="cv-legend-item cv-legend-gavel">
                  <Gavel size={13} aria-hidden="true" /> voted on later
                </span>
              </div>
              <div className="cv-sbs-rows">
                {stanceBySubject.list.map((e) => {
                  const fu = followUp.get(e.theme) || { topics: [], votes: [] };
                  const opposePct = stanceBySubject.maxArm
                    ? (e.oppose / stanceBySubject.maxArm) * 100 : 0;
                  const supportPct = stanceBySubject.maxArm
                    ? (e.support / stanceBySubject.maxArm) * 100 : 0;
                  const topicTip = fu.topics.length
                    ? `Discussed at a later meeting — Topics: “${fu.topics[0].title}”` +
                      (fu.topics.length > 1 ? ` (+${fu.topics.length - 1} more)` : '') +
                      '. Click to open the Topics tab.'
                    : '';
                  const voteTip = fu.votes.length
                    ? `Voted on ${formatDateOnly(fu.votes[0].voteDate)}: “${fu.votes[0].issue}”` +
                      (fu.votes.length > 1 ? ` (+${fu.votes.length - 1} more votes)` : '') +
                      '. Click to open the Voting tab.'
                    : '';
                  return (
                    <div className="cv-sbs-row" key={e.theme}>
                      <div className="cv-sbs-subject">
                        <button
                          type="button"
                          className={`cv-sbs-label ${themeFilter === e.theme ? 'active' : ''}`}
                          title={`${e.theme}${e.qualifier ? ` — ${e.qualifier}` : ''} (click to filter comments)`}
                          onClick={() => setThemeFilter(themeFilter === e.theme ? null : e.theme)}
                        >
                          {e.theme}
                          {e.qualifier && (
                            <span className="cv-sbs-qualifier"> — {e.qualifier}</span>
                          )}
                        </button>
                        <span className="cv-sbs-flags">
                          {fu.topics.length > 0 && (
                            <button
                              type="button"
                              className="cv-flag-btn cv-flag-discussed"
                              title={topicTip}
                              aria-label={topicTip}
                              onClick={() => navigate(`?tab=topics#topic-${fu.topics[0].id}`)}
                            >
                              <Flag size={14} aria-hidden="true" />
                            </button>
                          )}
                          {fu.votes.length > 0 && (
                            <button
                              type="button"
                              className="cv-flag-btn cv-flag-voted"
                              title={voteTip}
                              aria-label={voteTip}
                              onClick={() => navigate(`?tab=voting#vote-${fu.votes[0].id}`)}
                            >
                              <Gavel size={14} aria-hidden="true" />
                            </button>
                          )}
                        </span>
                      </div>
                      <div
                        className="cv-sbs-bars"
                        title={`${e.theme}: ${e.support} support · ${e.oppose} oppose`}
                      >
                        <div className="cv-sbs-half cv-sbs-half-left">
                          {e.oppose > 0 && <span className="cv-sbs-count">{e.oppose}</span>}
                          {e.oppose > 0 && (
                            <div className="cv-sbs-bar cv-sbs-bar-oppose" style={{ width: `${opposePct}%` }} />
                          )}
                        </div>
                        <div className="cv-sbs-axis" />
                        <div className="cv-sbs-half cv-sbs-half-right">
                          {e.support > 0 && (
                            <div className="cv-sbs-bar cv-sbs-bar-support" style={{ width: `${supportPct}%` }} />
                          )}
                          {e.support > 0 && <span className="cv-sbs-count">{e.support}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="cv-sbs-note">
                Counts include only comments with a clear support or oppose stance.
              </p>
            </div>
          )}

          {/* Themes over time */}
          {trend.topThemes.length > 0 && (
            <div className="cv-card cv-trend">
              <h3 className="cv-section-title">Themes over time</h3>
              <div className="cv-trend-legend">
                {trend.topThemes.map((t, i) => (
                  <span key={t} className="cv-legend-item">
                    <span className={`cv-swatch cv-theme-color-${i}`} />
                    {t}
                  </span>
                ))}
              </div>
              {trend.maxMonthTotal === 0 ? (
                <p className="cv-trend-empty">No comments in the last 6 months.</p>
              ) : (
                <div className="cv-trend-rows">
                  {trend.monthRows.map((m) => (
                    <div className="cv-trend-row" key={m.key}>
                      <span className="cv-trend-month">{m.label}</span>
                      <div className="cv-trend-bar">
                        {m.perTheme.map((c, i) =>
                          c > 0 ? (
                            <div
                              key={i}
                              className={`cv-trend-seg cv-theme-bg-${i}`}
                              style={{ width: `${(c / trend.maxMonthTotal) * 100}%` }}
                              title={`${trend.topThemes[i]}: ${c}`}
                            />
                          ) : null
                        )}
                      </div>
                      <span className="cv-trend-count">{m.total > 0 ? m.total : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Comment list grouped by meeting */}
          {groups.length === 0 ? (
            <div className="cv-card cv-empty">
              No comments match the current filters.
              {hasFiltersActive && (
                <button
                  type="button"
                  className="cv-clear-filters"
                  onClick={() => {
                    setThemeFilter(null);
                    setComplaintsOnly(false);
                    setStanceFilter('all');
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            groups.map((g) => (
              <div className="cv-card cv-meeting-group" key={g.date}>
                <h3 className="cv-meeting-date">
                  {g.date === 'unknown' ? 'Meeting date unknown' : `${formatDateOnly(g.date)} meeting`}
                </h3>
                <div className="cv-comment-list">
                  {g.items.map((r) => (
                    <div className="cv-comment" key={r.id}>
                      <div className="cv-comment-meta">
                        {r.speaker_role && <span className="cv-badge cv-role">{r.speaker_role}</span>}
                        {r.stance && (
                          <span className={`cv-badge ${stanceClass(r.stance)}`}>{r.stance}</span>
                        )}
                        {r.sentiment && (
                          <span
                            className={`cv-sent-dot ${sentimentClass(r.sentiment)}`}
                            title={`Sentiment: ${r.sentiment}`}
                          />
                        )}
                        {r.is_complaint && (
                          <span className="cv-badge cv-complaint">
                            {r.complaint_subject ? `Complaint: ${r.complaint_subject}` : 'Complaint'}
                          </span>
                        )}
                      </div>
                      {r.summary && <p className="cv-summary">{r.summary}</p>}
                      {r.quote && <blockquote className="cv-quote">&ldquo;{r.quote}&rdquo;</blockquote>}
                      {(r.agenda_item || r.timestamp) && (
                        <div className="cv-comment-foot">
                          {r.agenda_item && <span className="cv-agenda-tag">{r.agenda_item}</span>}
                          {r.timestamp && <span className="cv-timestamp">{r.timestamp}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}

export default CommunityVoice;
